import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sun, Moon, Menu, X, LogOut, LayoutDashboard, Shield, Search } from "lucide-react";
import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/language-switcher";
import { playNavigate } from "@/lib/ui-sounds";
import logoSrc from "@assets/primestream_logo_1772514087264.png";

export function NavHeader() {
  const { user, logout, isAdmin, isProvider } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [location, navigate] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  const initials = user?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  const isActive = (path: string) => location === path || location.startsWith(path + "/");

  const handleNavClick = useCallback(() => {
    playNavigate();
  }, []);

  const navLinks = [
    { href: "/calendar", label: t("nav.calendar"), testId: "link-calendar", show: true },
    { href: "/recordings", label: t("nav.recordings"), testId: "link-recordings", show: true },
    { href: "/provider", label: t("nav.myClasses"), testId: "link-provider-dashboard", show: isProvider },
    { href: "/admin", label: t("nav.admin"), testId: "link-admin-dashboard", show: isAdmin },
  ];

  return (
    <header className="sticky top-0 z-50 w-full glass-nav depth-shadow-md">
      <div className="mx-auto flex h-14 max-w-7xl items-center px-3 sm:px-4 md:px-6">
        <Link href="/" className="flex items-center gap-2.5 shrink-0 mr-4 lg:mr-6" onClick={handleNavClick}>
          <img
            src={logoSrc}
            alt="PrimeStream"
            className="h-8 w-auto object-contain"
            data-testid="img-logo"
          />
        </Link>

        <nav className="hidden md:flex items-center justify-center flex-1 gap-1 lg:gap-2">
          {navLinks.filter(l => l.show).map(link => (
            <Link
              key={link.href}
              href={link.href}
              data-testid={link.testId}
              onClick={handleNavClick}
              className={`nav-mode-link relative px-3.5 lg:px-4 py-1.5 rounded-lg text-[15px] lg:text-[16px] font-semibold tracking-[0.02em] transition-all duration-200 ${
                isActive(link.href)
                  ? "nav-mode-active text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 shrink-0 ml-auto md:ml-0">
          <div className="hidden lg:block w-36 xl:w-44">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                type="search"
                placeholder={t("nav.search")}
                className="h-8 pl-8 text-xs glass-input rounded-full"
                data-testid="input-search"
              />
            </div>
          </div>

          <LanguageSwitcher />
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={toggleTheme}
            data-testid="button-theme-toggle"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="button-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback
                      className="text-xs font-semibold text-white"
                      style={{
                        background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                        boxShadow: "0 0 10px rgba(59, 130, 246, 0.2)",
                      }}
                    >
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 glass-card">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                {isProvider && (
                  <DropdownMenuItem onClick={() => navigate("/provider")} data-testid="menu-provider">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    {t("nav.providerDashboard")}
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="mr-2 h-4 w-4" />
                    {t("nav.adminPanel")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("nav.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              className="btn-gradient-capsule text-sm btn-cta-glow"
              data-testid="button-login"
            >
              {t("nav.signIn")}
            </Button>
          )}

          <Button
            size="icon"
            variant="ghost"
            className="md:hidden h-8 w-8"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="button-mobile-menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>
      {mobileOpen && (
        <div className="border-t border-[var(--glass-border)] p-4 md:hidden" style={{ background: "rgba(3, 5, 16, 0.95)", backdropFilter: "blur(30px)", WebkitBackdropFilter: "blur(30px)", boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <nav className="flex flex-col gap-1">
            {navLinks.filter(l => l.show).map(link => (
              <Link
                key={link.href}
                href={link.href}
                className={`px-3 py-2.5 rounded-lg text-[15px] font-semibold tracking-[0.02em] transition-all duration-200 ${
                  isActive(link.href)
                    ? "nav-mode-active text-white"
                    : "text-muted-foreground"
                }`}
                onClick={() => { setMobileOpen(false); handleNavClick(); }}
                data-testid={`${link.testId}-mobile`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
